<template>
    <div class="px-2 md:px-4 pt-2 md:pt-14 pb-4 h-full">
        <div
            class="mb-4 p-4 flex items-center bg-gradient-to-br from-primary/5 to-purple-100 dark:from-neutral-700/50 dark:to-neutral-800/50 rounded-lg border border-gray-200 dark:border-neutral-700 shadow-sm gap-4 flex-col sm:flex-row md:flex-col lg:flex-row">

            <div class="flex gap-4 items-center">
                <!-- Icon -->
                <div class="flex items-center justify-center w-12 h-12 bg-primary_opc_less rounded-full flex-shrink-0">
                    <svg-vue icon="crown" class="w-6 h-6 text-primary"></svg-vue>
                </div>

                <!-- Content -->
                <div class="flex-1">
                    <h3 class="text-lg font-semibold text-gray-800 dark:text-white">
                        {{ $t('Boost your visibility by promoting your content') }}
                    </h3>
                    <p class="text-sm text-gray-600 dark:text-neutral-300 mt-1">
                        {{ $t('Promotions help your event stand out by appearing on our platform or on the websites of our partners — reaching more visitors, getting featured on our socials, and increasing ticket sales')}}.
                    </p>
                </div>
            </div>
        </div>

        <div v-if="!canEditPromotion"
            class="p-4 mb-4 flex items-start bg-gray-50 rounded-lg border border-gray-200 dark:border-neutral-700 dark:bg-neutral-800 shadow-sm gap-4 flex-col sm:flex-row md:flex-col lg:flex-row">
            <div class="flex gap-4 items-center">
                <div class="flex-1">
                    <span class="text-base font-semibold text-gray-800 dark:text-white capitalize">
                        {{ $t('Promotion cannot be editted') }}
                    </span>
                    <div class="text-xs text-gray-600 dark:text-neutral-300 mt-1">
                        {{ $t('promotion_cannot_edit') }}
                    </div>
                </div>
            </div>
        </div>
        <form ref="promotionpayment">
            <app-tabs :tabs-count="3" ref="promotionsetup" v-model="step" @step-change="onStepChange">
                
                <template #title-0>
                    {{ $t('Design') }}
                </template>
                <template #content-0>
                    <div class="w-full grid grid-cols-1 md:grid-cols-2 gap-6 my-4 h-fit">
                        <div class="w-full flex flex-col gap-6">
                            <div class="w-full">
                                <label for="type" class="block mb-1 text-sm font-medium text-gray-900 dark:text-white">
                                    {{ $t('Type') }}
                                </label>
                                <select id="type" v-model="promotionType" :disabled="!canEditPromotion"
                                    :class="[!canEditPromotion ? 'border-gray-100 bg-gray-200 cursor-not-allowed dark:bg-neutral-800 dark:border-neutral-600' : '']"
                                    class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary block w-full p-2.5 dark:bg-neutral-700 dark:border-neutral-700 dark:text-white">
                                    <option v-for="option in typeOptions" :key="option.value" :value="option.value">
                                        {{ option.label }}
                                    </option>
                                </select>

                                <sub v-if="errors.type" class="text-danger">
                                    <span class="input-error">{{ errors.type }}</span>
                                </sub>
                            </div>
                            <div
                                class="p-4 flex items-center bg-gray-50 rounded-lg border border-gray-200 dark:border-neutral-700 dark:bg-neutral-800 shadow-sm gap-4 flex-col sm:flex-row md:flex-col lg:flex-row">
                                <div class="flex gap-4 items-center">
                                    <div class="flex-1">
                                        <span class="text-base font-semibold text-gray-800 dark:text-white capitalize">
                                            {{ promotionType }} {{ $t('promotion') }}
                                        </span>
                                        <div v-if="promotionType == 'spotlight'"
                                            class="text-xs text-gray-600 dark:text-neutral-300 mt-1">
                                            {{ $t("Your item will always appear among the first in its category, both on our website and on our partners’ websites") }}.<br />
                                            {{ $t("Our partners, including local hotels, showcase your item to new tourists, ensuring it’s one of the first things they see") }}.
                                        </div>
                                        <div v-else-if="promotionType == 'hero'"
                                            class="text-xs text-gray-600 dark:text-neutral-300 mt-1">
                                            {{ $t("Your item will be featured in the most prominent position on the homepage of our website") }}.<br />
                                            {{ $t("This ensures maximum visibility to all visitors, making your item one of the first things they see") }}.
                                        </div>
                                        <div v-else-if="promotionType == 'notification'"
                                            class="text-xs text-gray-600 dark:text-neutral-300 mt-1">
                                            {{ $t("Your item will be highlighted with a special notification on our homepage") }}.<br />
                                            {{ $t("This draws immediate attention from visitors, ensuring your item stands out prominently") }}.
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div v-if="promotionType == 'hero' || promotionType == 'notification'">
                                <label for="customTitle"
                                    class="block mb-1 text-sm font-medium text-gray-900 dark:text-white">
                                    {{ $t('Title') }}</label>
                                <input type="text" id="customTitle" name="customTitle" v-model="customTitle"
                                    :disabled="!canEditPromotion"
                                    :class="[!canEditPromotion ? 'border-gray-100 bg-gray-200 cursor-not-allowed dark:bg-neutral-800 dark:border-neutral-600' : '']"
                                    class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 dark:bg-neutral-700 dark:border-neutral-700 dark:placeholder-neutral-400 dark:text-white"
                                    :placeholder="$t('Title') + '...'" required />
                            </div>
                            <template v-if="promotionType == 'hero'">
                                <div class="flex gap-2 flex-wrap" v-if="canEditPromotion">
                                    <div @click="toggleItem('addSubtitle')"
                                        class="bg-primary_opc_less flex items-center justify-between text-xxs text-primary px-2 py-1 leading-3 rounded-full border border-primary hover:bg-primary-lighter hover:scale-105 transition cursor-pointer">
                                        <span v-if="!addSubtitle" class="text-xxs">{{ $t('Add subtitle') }}</span>
                                        <span v-else class="text-xxs">{{ $t('Remove subtitle') }}</span>
                                        <svg-vue icon="plus" class="ml-1 w-3 h-3 transition"
                                            :class="[addSubtitle ? 'rotate-45' : '']"></svg-vue>
                                    </div>
                                    <div @click="toggleItem('addBtnText')"
                                        class="bg-primary_opc_less flex items-center justify-between text-xxs text-primary px-2 py-1 leading-3 rounded-full border border-primary hover:bg-primary-lighter hover:scale-105 transition cursor-pointer">
                                        <span v-if="!addBtnText" class="text-xxs">{{ $t('Change button text') }}</span>
                                        <span v-else class="text-xxs">{{ $t('use default button text') }}</span>
                                        <svg-vue icon="plus" class="ml-1 w-3 h-3 transition"
                                            :class="[addBtnText ? 'rotate-45' : '']"></svg-vue>
                                    </div>
                                    <div @click="toggleItem('addImage')"
                                        class="bg-primary_opc_less flex items-center justify-between text-xxs text-primary px-2 py-1 leading-3 rounded-full border border-primary hover:bg-primary-lighter hover:scale-105 transition cursor-pointer">
                                        <span v-if="!addImage" class="text-xxs">{{ $t('Change background image') }}</span>
                                        <span v-else class="text-xxs">{{ $t('Use default background image') }}</span>
                                        <svg-vue icon="plus" class="ml-1 w-3 h-3 transition"
                                            :class="[addImage ? 'rotate-45' : '']"></svg-vue>
                                    </div>
                                    <div @click="toggleItem('addSecondaryBtn')"
                                        class="bg-primary_opc_less flex items-center justify-between text-xxs text-primary px-2 py-1 leading-3 rounded-full border border-primary hover:bg-primary-lighter hover:scale-105 transition cursor-pointer">
                                        <span v-if="!addSecondaryBtn" class="text-xxs">{{ $t('Add secondary button')
                                        }}</span>
                                        <span v-else class="text-xxs">{{ $t('Remove secondary button') }}</span>
                                        <svg-vue icon="plus" class="ml-1 w-3 h-3 transition"
                                            :class="[addSecondaryBtn ? 'rotate-45' : '']"></svg-vue>
                                    </div>
                                </div>
                                <div v-if="addSubtitle">
                                    <label for="subTitle"
                                        class="block mb-1 text-sm font-medium text-gray-900 dark:text-white">
                                        {{ $t('Subtitle') }}</label>
                                    <input type="text" id="subTitle" name="subTitle" v-model="subTitle"
                                        :disabled="!canEditPromotion"
                                        :class="[!canEditPromotion ? 'border-gray-100 bg-gray-200 cursor-not-allowed dark:bg-neutral-800 dark:border-neutral-600' : '']"
                                        class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 dark:bg-neutral-700 dark:border-neutral-700 dark:placeholder-neutral-400 dark:text-white"
                                        :placeholder="$t('Subtitle') + '...'" required />
                                </div>
                                <div v-if="addBtnText">
                                    <label for="cta_1_text"
                                        class="block mb-1 text-sm font-medium text-gray-900 dark:text-white">
                                        {{ $t('Button text') }}</label>
                                    <input type="text" id="cta_1_text" name="cta_1_text" v-model="cta_1_text"
                                        :disabled="!canEditPromotion"
                                        :class="[!canEditPromotion ? 'border-gray-100 bg-gray-200 cursor-not-allowed dark:bg-neutral-800 dark:border-neutral-600' : '']"
                                        class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 dark:bg-neutral-700 dark:border-neutral-700 dark:placeholder-neutral-400 dark:text-white"
                                        :placeholder="$t('Subtitle') + '...'" required />
                                </div>
                                <div v-if="addImage">
                                    <label for="image" class="block mb-1 text-sm font-medium text-gray-900 dark:text-white">
                                        {{ $t('Image') }}
                                    </label>
                                    <input type="file" :disabled="!canEditPromotion" id="image" name="image"
                                        accept="image/png, image/jpeg, image/jpg, image/webp"
                                        :class="[!canEditPromotion ? 'border-gray-100 bg-gray-200 cursor-not-allowed dark:bg-neutral-800 dark:border-neutral-600' : '']"
                                        @change="previewFiles" ref="fileInput" class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full 
                                            file:mr-4 file:ml-0 file:py-2.5 file:px-4 file:rounded-lg file:border-0 
                                            file:text-sm file:font-medium file:bg-primary-lightest file:text-primary
                                            hover:file:bg-primary-lighter dark:bg-neutral-700 dark:border-neutral-700 
                                            dark:text-white dark:file:bg-neutral-600 dark:file:text-neutral-200 
                                            dark:hover:file:bg-neutral-500" />
                                </div>
                                <div v-if="addSecondaryBtn">
                                    <label for="cta_2_text"
                                        class="block mb-1 text-sm font-medium text-gray-900 dark:text-white">
                                        {{ $t('Secondary button text') }}</label>
                                    <input type="text" id="cta_2_text" name="cta_2_text" v-model="cta_2_text"
                                        :disabled="!canEditPromotion"
                                        :class="[!canEditPromotion ? 'border-gray-100 bg-gray-200 cursor-not-allowed dark:bg-neutral-800 dark:border-neutral-600' : '']"
                                        class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 dark:bg-neutral-700 dark:border-neutral-700 dark:placeholder-neutral-400 dark:text-white"
                                        :placeholder="$t('more info') + '...'" required />
                                </div>
                                <div v-if="addSecondaryBtn">
                                    <label for="cta_2_url"
                                        class="block mb-1 text-sm font-medium text-gray-900 dark:text-white">
                                        {{ $t('Secondary button url') }}</label>
                                    <input type="url" id="cta_2_url" name="cta_2_url" v-model="cta_2_url"
                                        :disabled="!canEditPromotion"
                                        :class="[!canEditPromotion ? 'border-gray-100 bg-gray-200 cursor-not-allowed dark:bg-neutral-800 dark:border-neutral-600' : '']"
                                        class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 dark:bg-neutral-700 dark:border-neutral-700 dark:placeholder-neutral-400 dark:text-white"
                                        placeholder="https://..." required />
                                </div>
                            </template>

                        </div>
                        <div class="w-full flex flex-col gap-6">
                            <div class="w-full">
                                <span class="block text-sm font-medium text-gray-900 dark:text-white">
                                    {{ $t('Preview') }}:
                                </span>
                                <div class="text-xxs opacity-50 mb-1 dark:text-white">
                                    {{ $t('For example purposes only') }}; {{ $t('not binding') }}
                                </div>
                                <div class="min-h-[310px] w-full rounded-lg bg-gray-50 overflow-hidden rounded-lg border border-gray-200 dark:border-neutral-700 shadow-sm"
                                    :class="[promotionType == 'notification' ? 'min-h-[320px]' : '']">
                                    <div v-if="promotionType == 'notification'"
                                        class="relative flex w-full bg-primary text-white">
                                        <div class="text-xxs w-full text-center py-1 z-5">{{ customTitle }}</div>
                                    </div>
                                    <div class="h-12 w-full bg-white shadow flex justify-between items-center">
                                        <img class="h-8 ml-2" :src="client.image_link">
                                        <div class="bg-gray-200 rounded-full h-5 w-20"></div>
                                        <div class="flex gap-2 mr-2">
                                            <span class="text-xxs">link 1</span>
                                            <span class="text-xxs">link 2</span>
                                        </div>
                                    </div>

                                    <div v-if="promotionType == 'hero'"
                                        class="relative w-[90%] mt-4 h-32 mx-auto bg-primary rounded-lg shadow-lg z-1">
                                        <div class="absolute bottom-2 left-2 z-5 flex flex-col text-white max-w-[50%]">
                                            <div class="text-sm font-bold leading-4">{{ customTitle }}</div>
                                            <div class="mt-1 text-xxs leading-3">{{ subTitle }}</div>
                                            <div class="flex gap-1">
                                                <div class="mt-1 bg-white rounded-full h-4 px-2 w-fit flex items-center justify-center text-gray-800"
                                                    style="font-size:7px;">{{ cta_1_text }}</div>
                                                <div v-if="cta_2_text && cta_2_url"
                                                    class="mt-1 border border-white rounded-full h-4 px-2 w-fit flex items-center justify-center text-white"
                                                    style="font-size:7px;">{{ cta_2_text }}</div>
                                            </div>
                                        </div>
                                        <div
                                            class="absolute z-[1] bg-gradient-to-tr from-black/50 to-transparent h-full w-full rounded-lg">
                                        </div>
                                        <label for="image"
                                            class="flex flex-col items-center justify-center w-full rounded-lg cursor-pointer js_file_input_preview h-full relative overflow-hidden"
                                            ref="fileLabel">
                                            <div class="flex flex-col items-center justify-center pt-5 pb-6 px-4 js_skeleton z-10 w-full h-full transition pointer-events-none"
                                                :class="[fileUsed || $attrs.currentvalue ? 'opacity-0 group-hover:opacity-100 text-white' : '']">
                                            </div>
                                            <img v-if="addImage && loadedImage" :src="loadedImage"
                                                class="w-full h-full rounded-md object-cover absolute top-0 left-0 z-0 transition hover:blur-md hover:scale-105 hover:brightness-50" />
                                            <img v-else :src="default_image"
                                                class="w-full h-full rounded-md object-cover absolute top-0 left-0 z-0 transition hover:blur-md hover:scale-105 hover:brightness-50" />
                                        </label>
                                    </div>
                                    <div v-else class="w-[90%] mt-4 h-32 mx-auto bg-white rounded-lg shadow-lg">
                                    </div>

                                    <div class="promo-line w-[90%] mx-auto h-16 flex gap-2 mt-6 overflow-hidden"
                                        :class="[promotionType == 'spotlight' ? 'animate-line' : '']">
                                        <div class="box bg-gray-200 rounded-lg w-1/5 h-full"></div>
                                        <div class="box bg-gray-200 rounded-lg w-1/5 h-full"></div>
                                        <div class="box bg-gray-200 rounded-lg w-1/5 h-full"></div>
                                        <div class="box special bg-gray-200 rounded-lg w-1/5 h-full"></div>
                                        <div class="box bg-gray-200 rounded-lg w-1/5 h-full"></div>
                                    </div>
                                </div>

                                <template v-if="promotionType == 'spotlight'">
                                    <div class="text-xxs mt-6 opacity-50 mb-1">{{ $t('Partner') }} {{ $t('websites') }}:</div>
                                    <div
                                        class="min-h-[115px] w-full rounded-lg bg-gray-50 overflow-hidden rounded-lg border border-gray-200 dark:border-neutral-700 shadow-sm ">
                                        <div
                                            class="promo-line w-[90%] mx-auto h-16 flex gap-2 mt-6 overflow-hidden animate-line">
                                            <div class="box bg-gray-200 rounded-lg w-1/5 h-full"></div>
                                            <div class="box bg-gray-200 rounded-lg w-1/5 h-full"></div>
                                            <div class="box bg-gray-200 rounded-lg w-1/5 h-full"></div>
                                            <div class="box special bg-gray-200 rounded-lg w-1/5 h-full"></div>
                                            <div class="box bg-gray-200 rounded-lg w-1/5 h-full"></div>
                                        </div>
                                    </div>
                                </template>
                            </div>
                        </div>
                    </div>
                </template>
                
                <template #title-1>
                    {{ $t('General') }}
                </template>
                <template #content-1>
                    <div class="grid gap-6 mt-4 md:grid-cols-2 h-fit">
                        <div>
                            <label for="Title" class="block mb-1 text-sm font-medium text-gray-900 dark:text-white">
                                {{ $t('Promotion name') }}</label>
                            <input type="text" id="title" name="title" v-model="name" :disabled="true"
                                class="bg-gray-100 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 dark:bg-neutral-700 dark:border-neutral-700 dark:placeholder-neutral-400 dark:text-white cursor-not-allowed"
                                :placeholder="$t('Promotion for') + '...'" required />
                        </div>
                        <div></div>
                        <div class="col-span-2 flex gap-6">
                            <!-- Start Option -->
                            <div class="space-y-6">
                                <!-- Promotion Start Date -->
                                <div>
                                    <label class="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                                        {{ $t('Promotion Start Date') }}
                                    </label>

                                    <div class="flex items-center gap-4">
                                        <!-- Option 1: Today -->
                                        <label class="flex items-center gap-2 cursor-pointer">
                                            <input type="radio" value="today" v-model="startOption"
                                                class="text-primary focus:ring-primary"
                                                :class="[!canEditPromotion ? 'border-gray-100 bg-gray-200 cursor-not-allowed dark:bg-neutral-800 dark:border-neutral-600' : '']"
                                                :disabled="!canEditPromotion" />
                                            <span class="text-sm text-gray-800 dark:text-gray-200">{{ $t('Today') }}</span>
                                        </label>

                                        <!-- Option 2: Specific Date -->
                                        <label class="flex items-center gap-2 cursor-pointer">
                                            <input type="radio" value="specific" v-model="startOption"
                                                class="text-primary focus:ring-primary"
                                                :class="[!canEditPromotion ? 'border-gray-100 bg-gray-200 cursor-not-allowed dark:bg-neutral-800 dark:border-neutral-600' : '']"
                                                :disabled="!canEditPromotion" />
                                            <span class="text-sm text-gray-800 dark:text-gray-200">{{ $t('Specific Date')
                                            }}</span>
                                        </label>
                                    </div>

                                    <!-- Date Picker -->
                                    <div v-if="startOption === 'specific'" class="mt-3">
                                        <input type="date" v-model="startDate" :min="minDate"
                                            :class="[!canEditPromotion ? 'border-gray-100 bg-gray-200 cursor-not-allowed dark:bg-neutral-800 dark:border-neutral-600' : '']"
                                            class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary block w-64 p-2.5 dark:bg-neutral-700 dark:border-neutral-700 dark:text-white"
                                            :disabled="!canEditPromotion" />
                                    </div>
                                </div>

                                <!-- Duration in Weeks -->
                                <div>
                                    <label class="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                                        {{ $t('Promotion Duration') }}
                                    </label>

                                    <select v-model="weeks"
                                        :class="[!canEditPromotion ? 'border-gray-100 bg-gray-200 cursor-not-allowed dark:bg-neutral-800 dark:border-neutral-600' : '']"
                                        class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary block w-64 p-2.5 dark:bg-neutral-700 dark:border-neutral-700 dark:text-white"
                                        :disabled="!canEditPromotion">
                                        <option v-for="n in 8" :key="n" :value="n">
                                            {{ n }} {{ n === 1 ? $t('week') : $t('weeks') }}
                                        </option>
                                    </select>
                                </div>
                            </div>


                            <!-- Preview -->
                            <div class="flex flex-col justify-end text-sm text-gray-700 dark:text-gray-300">
                                <p>
                                    {{ $t('Starts on') }}:
                                    <strong>{{ formattedStart }}</strong>
                                </p>
                                <p>
                                    {{ $t('Ends on') }}:
                                    <strong>{{ formattedEnd }}</strong>
                                </p>
                            </div>
                        </div>
                        <div class="hidden">
                            <label for="resource_type" class="block mb-1 text-sm font-medium text-gray-900 dark:text-white">
                                {{ $t('Promotion type') }}
                            </label>
                            <input type="text" id="resource_type" name="resource_type" readonly="readonly"
                                v-model="resourceType" :disabled="!canEditPromotion"
                                class="bg-gray-200 border border-gray-300 text-gray-500 text-sm rounded-lg block w-full p-2.5 cursor-not-allowed dark:bg-neutral-700 dark:border-neutral-600 dark:text-neutral-400 focus:outline-none focus:ring-0 focus:border-gray-300 pointer-events-none"
                                :placeholder="$t('Promotion type') + '...'" required />
                        </div>
                        <div class="hidden">
                            <label for="item_id" class="block mb-1 text-sm font-medium text-gray-900 dark:text-white">
                                {{ $t('Promotion item') }}
                            </label>
                            <input type="text" id="item_id" name="item_id" readonly="readonly" v-model="itemId"
                                :disabled="!canEditPromotion"
                                class="bg-gray-200 border border-gray-300 text-gray-500 text-sm rounded-lg block w-full p-2.5 cursor-not-allowed dark:bg-neutral-700 dark:border-neutral-600 dark:text-neutral-400 focus:outline-none focus:ring-0 focus:border-gray-300 pointer-events-none"
                                :placeholder="$t('Promotion item') + '...'" required />
                        </div>
                    </div>
                </template>

                <template #title-2>
                    {{ $t('Payment') }}
                </template>
                <template #content-2>

                    <div class="w-full mt-6 bg-white dark:bg-neutral-800 border-b border-gray-200 dark:border-neutral-700">
                        <div class="max-w-7xl mx-auto px-4 py-4 flex justify-between items-end">
                            <div class="dark:text-white flex flex-col">
                                <span class="text-lg font-bold capitalize">{{ customTitle }}</span>
                                <span class="opacity-50 capitalize">{{ resourceType }} {{ promotionType }} {{
                                    $t('promotion') }}</span>
                                <span class="opacity-80 text-sm capitalize">{{ $tc('for_week', weeks, { count: weeks })
                                }}</span>
                            </div>
                            <div class="text-lg md:text-xl font-semibold text-gray-900 dark:text-white">
                                €{{ formatPrice(amount) }}
                                <span class="text-sm font-normal text-gray-500 dark:text-neutral-400">/ {{ $t('total')
                                }}</span>
                            </div>
                        </div>
                    </div>

                    <stripe-element-payment class="mt-6 min-h-[130px]"
                        v-if="elementOptionsComp.clientSecret && confirmParams.payment_method_data.billing_details.name"
                        v-show="!stripeLoading" ref="paymentRef" :pk="stripe_key" :elements-options="elementOptionsComp"
                        :create-options="createStripeOptions" :confirm-params="confirmParams"
                        :metadata="confirmParams.payment_method_data.metadata"
                        :receipt_email="confirmParams.payment_method_data.billing_details.email"
                        @error="stripeErrorListener" @element-ready="stripeReady()" @element-change="stripeChange()" />

                    <div class="flex items-start mb-6 mt-6 h-fit" v-if="canEditPromotion">
                        <div class="flex items-center h-5">
                            <input id="remember" type="checkbox" value=""
                                class="w-4 h-4 border border-gray-300 rounded-sm checked:bg-primary bg-gray-50 focus:ring-3 focus:ring-blue-300 dark:bg-neutral-700 dark:border-neutral-700 dark:focus:ring-blue-600 dark:ring-offset-gray-800"
                                required />
                        </div>
                        <label for="remember" class="ms-2 text-sm font-medium text-gray-900 dark:text-neutral-300">
                            {{ $t('I agree with the') }}
                            <a :href="$baseUrl + '/termsandconditions'" target="_blank"
                                class="text-primary hover:underline dark:text-primary-dark">
                                {{ $t('terms and conditions') }}</a>.
                        </label>
                    </div>
                    <div v-else class="dark:text-white opacity-50 px-4 py-4">
                        {{ $t('promotion_cannot_edit') }}
                    </div>
                </template>
            </app-tabs>

            <div class="w-full flex justify-between mt-2 md:mt-4">
                <div v-if="step > 0" @click="back()"
                    class="text-white bg-gray-400 dark:bg-neutral-600 hover:bg-gray-600 dark:hover:bg-neutral-500 cursor-pointer focus:ring-4 focus:outline-none font-medium rounded-lg text-sm w-fit px-5 py-2.5 text-center">
                    {{ $t('Back') }}
                </div>
                <div v-else class="w-fit"></div>

                <div v-if="step == 0" @click="next()"
                    class="text-white bg-primary hover:bg-primary-dark cursor-pointer focus:ring-4 focus:outline-none font-medium rounded-lg text-sm w-fit px-5 py-2.5 text-center">
                    {{ $t('Next') }}
                </div>
                <div v-else-if="step == 1 && canEditPromotion" @click="next()"
                    class="text-white bg-primary hover:bg-primary-dark cursor-pointer focus:ring-4 focus:outline-none font-medium rounded-lg text-sm w-fit px-5 py-2.5 text-center">
                    {{ $t('Save and confirm') }}
                </div>
                <div v-else-if="step == 1" @click="next()"
                    class="text-white bg-primary hover:bg-primary-dark cursor-pointer focus:ring-4 focus:outline-none font-medium rounded-lg text-sm w-fit px-5 py-2.5 text-center">
                    {{ $t('Next') }}
                </div>
                <button type="submit" v-else-if="step == 2 && canEditPromotion" @click.prevent="next()"
                    class="text-white bg-primary hover:bg-primary-dark cursor-pointer focus:ring-4 focus:outline-none font-medium rounded-lg text-sm w-fit px-5 py-2.5 text-center">
                    {{ $t('Go to payment') }}
                </button>
                <div v-else class="w-fit"></div>
            </div>

        </form>

    </div>
</template>
    
<script>
import AppAccordion from "../../../components/elements/appAccordion.vue";
import AppTabs from "../../../components/elements/appTabs.vue";

import { StripeElementPayment } from '@vue-stripe/vue-stripe';

export default {
    components: {
        AppAccordion, AppTabs, StripeElementPayment
    },
    props: {
        client: {
            type: Object,
            default: () => { },
        },
    },
    data() {
        return {
            promotionUuid: null,
            step: 0,
            resourceType: "",
            name: "",
            customTitle: "",
            status: "pending",
            subTitle: "",
            cta_1_text: "Click here",
            cta_2_text: "",
            cta_2_url: "",
            itemId: "",
            startOption: "today",
            loadedImage: "",
            startDate: null,
            promotionType: 'spotlight',
            weeks: 1,
            fileUsed: false,
            errors: [],
            default_image: null,
            typeOptions: [
                { value: 'spotlight', label: this.$t('Spotlight') },
                { value: 'hero', label: this.$t('Hero Homepage Banner') },
                { value: 'notification', label: this.$t('Notification Bar') },
            ],
            resource: null,
            addSubtitle: false,
            addBtnText: false,
            addImage: false,
            addSecondaryBtn: false,
            loadingStripe: 0,
            stripe_key: null,
            stripeLoading: true,
            stripeError: false,
            createStripeOptions: {
                layout: {
                    type: 'tabs',
                    defaultCollapsed: false,
                    radios: false,
                    spacedAccordionItems: true
                },
                fields: {
                    billingDetails: {
                        name: 'never'
                    }
                }
            },
            elementsOptions: {
                clientSecret: '',
                appearance: {
                    theme: 'stripe',
                    variables: {
                        // colorPrimary: JSON.parse(this.client).main_color,
                    },
                }, // appearance options
            },
            confirmParams: {
                return_url: this.$baseUrl + '/dashboard/payment/result',
                payment_method_data: {
                    billing_details: {
                        name: '',
                        email: '',
                    },
                    metadata: '',
                }
            },
        }
    },
    computed: {
        minDate() {
            return new Date().toISOString().split("T")[0];
        },
        elementOptionsComp: function () {
            this.loadingStripe
            return this.elementsOptions
        },
        canEditPromotion() {
            // Returns true if status is NOT one of the disallowed statuses
            return !['confirmed', 'cancelled', 'reviewRequired'].includes(this.status);
        },
        actualStartDate() {
            return this.startOption === "today"
                ? new Date()
                : this.startDate
                    ? new Date(this.startDate)
                    : new Date();
        },
        actualEndDate() {
            const end = new Date(this.actualStartDate);
            end.setDate(end.getDate() + this.weeks * 7);
            return end;
        },
        formattedStart() {
            return this.actualStartDate.toLocaleDateString();
        },
        formattedEnd() {
            return this.actualEndDate.toLocaleDateString();
        },
        pricePerWeek() {
            const monthlyPrices = {
                notification: 40000,
                spotlight: 6000,
                hero: 30000,
            };

            const monthly = monthlyPrices[this.promotionType] || 0;
            return Math.ceil(monthly / 4); // approximate weekly price
        },

        // Total amount for selected weeks
        amount() {
            return this.weeks * this.pricePerWeek;
        }
    },
    created() {
        this.adjustStartDate();
    },
    methods: {
        back() {
            if (this.step > 0) this.step--;
            this.savePromotion();
        },
        next() {
            if (this.step == 2) {
                if (!this.$refs.promotionpayment.checkValidity()) {
                    console.log(this.$refs.promotionpayment.reportValidity());
                    return;
                }
                this.$refs.paymentRef.submit();
            }
            if (this.step < 2) this.step++;
            this.savePromotion();
        },
        adjustStartDate() {
            const today = new Date();
            today.setHours(0, 0, 0, 0); // normalize to midnight for comparison

            if (this.startDate) {
                const startDateObj = new Date(this.startDate);
                startDateObj.setHours(0, 0, 0, 0);

                if (startDateObj < today && this.canEditPromotion) {
                    this.startOption = "today";
                    this.startDate = today;
                }
            } else if (this.canEditPromotion) {
                // If no startDate yet, default to today
                this.startOption = "today";
                this.startDate = today;
            }
        },
        formatPrice(amount) {
            return (amount / 100).toFixed(2);
        },
        onStepChange(newStep) {
            this.step = newStep;
        },
        previewFiles(e) {
            const input = this.$refs.fileInput;
            const label = this.$refs.fileLabel;

            if (!label || !input || !input.files.length) return;

            // Remove old previews
            label.querySelectorAll('img.preview-temp').forEach(img => img.remove());

            for (let i = 0; i < input.files.length; i++) {
                const file = input.files[i];
                const reader = new FileReader();

                reader.onload = (event) => {
                    const img = document.createElement('img');
                    img.src = event.target.result;

                    img.classList.add(
                        'preview-temp',
                        'w-full',
                        'h-full',
                        'rounded-md',
                        'object-cover',
                        'absolute',
                        'top-0',
                        'left-0',
                        'z-0',
                        'transition',
                        'hover:blur-sm',
                        'hover:scale-105',
                        'hover:brightness-50'
                    );

                    label.appendChild(img);
                };

                reader.readAsDataURL(file);
            }

            label.classList.remove('border-2');
            this.fileUsed = true;
        },
        async fetchItem() {
            try {
                this.loading = true;

                axios.get(`/api/dashboard/promotions/get/${this.promotionUuid}`)
                    .then(res => {
                        this.populateForm(res.data);
                    })
                    .catch(err => {
                        console.error(err);
                        this.$toast.error('Failed to load promotion.');
                    });
            } catch (error) {
                if (error.response?.status === 401) {
                    // User is unauthenticated → refresh token or redirect to login
                    console.warn('User unauthenticated. Attempting refresh...');
                    window.location.reload();
                } else {
                    console.error('Error fetching promotion:', error);
                }
            } finally {
                this.loading = false;
            }
        },
        async fetchResourceItem() {
            try {
                this.loading = true;


                if (!this.resourceType || !this.itemId) {
                    console.warn("Missing resourceType or itemId");
                    this.loading = false;
                    return;
                }
                const resourceMap = {
                    property: 'properties',
                    room: 'rooms',
                    event: 'events',
                    location: 'locations',
                    news: 'news'
                };

                const pluralType = resourceMap[this.resourceType] || this.resourceType + 's';
                const url = `/api/dashboard/${pluralType}?id=${this.itemId}`;

                const res = await axios.get(url, {
                    withCredentials: true,
                });

                this.resource = res.data?.data?.[0]?.resource;
            } catch (error) {
                console.error("Failed to fetch item:", error);
            } finally {
                this.loading = false;
            }
        },
        updateHeight() {
            this.$nextTick(() => {
                if (this.$refs.promotionsetup?.recalculateHeight) {
                    this.$refs.promotionsetup.recalculateHeight();
                }
            });
        },
        toggleItem(prop) {
            if (prop in this) {
                this[prop] = !this[prop];

                if (prop == 'addBtnText' && !this.addBtnText) {
                    this.cta_1_text = 'Click here';
                }
                if (prop == 'addSubtitle' && !this.addSubtitle) {
                    this.subTitle = '';
                }
                if (prop == 'addSecondaryBtn' && !this.addSecondaryBtn) {
                    this.cta_2_text = '';
                    this.cta_2_url = '';
                }
                if (prop == 'addImage' && !this.addImage) {
                    this.$refs.fileLabel.querySelectorAll('img.preview-temp').forEach(img => img.remove());
                }

                this.updateHeight();     // update UI if needed
            }
        },
        async populateForm(promotion) {
            if (!promotion) return;

            // Set basic IDs
            this.promotionUuid = promotion.uuid || '';
            this.itemId = promotion.item_id || '';
            this.resourceType = promotion.resource_type || '';
            this.promotionType = promotion.type || 'notification';

            // Set text fields
            this.name = promotion.title?.en || '';
            this.customTitle = promotion.custom_title?.en || promotion.title?.en;
            this.subTitle = promotion.subtitle?.en || '';
            this.cta_1_text = promotion.cta_1_text?.en || 'Click here';
            this.cta_2_text = promotion.cta_2_text?.en || '';
            this.cta_2_url = promotion.cta_2_target || '';

            this.loadedImage = promotion.image || "";
            this.status = promotion.status;

            // Dates
            if (promotion.start) {
                this.startOption = 'custom';
                this.startDate = promotion.start.split(' ')[0];
            } else {
                this.startOption = 'today';
                this.startDate = null;
            }

            if (promotion.end && promotion.start) {
                const start = new Date(promotion.start);
                const end = new Date(promotion.end);
                this.weeks = Math.ceil((end - start) / (1000 * 60 * 60 * 24 * 7));
            } else {
                this.weeks = 1;
            }

            // Flags
            this.addSubtitle = !!promotion.subtitle?.en;
            this.addBtnText = !!promotion.cta_1_text?.en;
            this.addSecondaryBtn = !!promotion.cta_2_text?.en || !!promotion.cta_2_target;
            this.addImage = !!promotion.image_link;

            // Default image
            this.default_image = promotion.image_link || null;

            // Update UI if needed
            this.updateHeight();
        },

        async savePromotion() {
            if (!this.canEditPromotion) { return };
            try {
                // Create FormData to handle text fields + optional file
                const formData = new FormData();

                formData.append('uuid', this.promotionUuid || ''); // for edit, empty for new
                formData.append('name', this.name);
                formData.append('customTitle', this.customTitle);
                formData.append('subTitle', this.subTitle);
                formData.append('cta_1_text', this.cta_1_text);
                formData.append('cta_2_text', this.cta_2_text);
                formData.append('cta_2_url', this.cta_2_url);
                formData.append('startOption', this.startOption);
                formData.append('startDate', this.startDate || '');
                formData.append('promotionType', this.promotionType);
                formData.append('weeks', this.weeks);
                formData.append('resourceType', this.resourceType);
                formData.append('itemId', this.itemId);
                formData.append('step', this.step);
                formData.append('addSubtitle', this.addSubtitle ? 1 : 0);
                formData.append('addBtnText', this.addBtnText ? 1 : 0);
                formData.append('addImage', this.addImage ? 1 : 0);
                formData.append('addSecondaryBtn', this.addSecondaryBtn ? 1 : 0);

                // If an image is selected
                if (this.$refs.fileInput && this.$refs.fileInput.files.length > 0) {
                    formData.append('image', this.$refs.fileInput.files[0]);
                }

                if (this.step === 2) {
                    formData.append('amount', this.amount); // amount in cents or euros (depending on backend)
                }

                const response = await axios.post('/api/dashboard/promotions/save', formData, {
                    withCredentials: true,
                });

                // ✅ If backend returns a PaymentIntent client secret, store it
                if (response.data.payment) {
                    if (this.confirmParams.payment_method_data.metadata.payment_uuid != response.data.payment.uuid) {
                        this.stripe_key = response.data.payment.stripe_public;

                        // Set the Stripe client secret for Elements
                        this.elementsOptions.clientSecret = response.data.payment.payment_intent_secret;
                        const isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
                        if (isDarkMode) {
                            this.elementsOptions.appearance.theme = 'night';
                            this.elementsOptions.appearance.variables.colorBackground = '#262626';
                        }

                        // Set appearance variables, e.g., client color
                        this.elementsOptions.appearance.variables.colorPrimary = response.data.payment.clientColor;

                        // Set billing details for confirmation
                        this.confirmParams.payment_method_data.billing_details.name = `${response.data.payment.user.first_name} ${response.data.payment.user.last_name}`;
                        this.confirmParams.payment_method_data.billing_details.email = response.data.payment.user.email;

                        // Optional: you can also add metadata for Stripe
                        this.confirmParams.payment_method_data.metadata = {
                            payment_uuid: response.data.payment.uuid,
                            type: 'PROMOTION',
                            clientID: response.data.payment.clientID,
                            amount: response.data.payment.amount,
                            currency: response.data.payment.currency,
                            resource_uuid: response.data.payment.resource_uuid,
                            resource_type: response.data.payment.resource_type
                        };
                    }
                }

                if (response.data.success) {
                    this.$toast.success(this.$t('Promotion saved successfully!'));
                    this.promotionUuid = response.data.uuid;
                    this.$router.replace({ name: 'promotions.edit', params: { uuid: response.data.uuid } });
                } else {
                    this.$toast.error(this.$t('Failed to save promotion.'));
                }
            } catch (error) {
                console.error('Error saving promotion:', error);
                this.$toast.error('An error occurred while saving promotion.');
            }
        },
        stripeErrorListener(error) {
            Vue.$toast.error(error.message)
        },
        stripeChange() {
            this.stripeError = false;
            this.updateHeight();
            setTimeout(() => {
                this.updateHeight();
            }, 300);
        },
        stripeReady() {
            this.stripeLoading = false;
            this.updateHeight();
        },
    },
    watch: {
        startOption(newVal) {
            if (newVal === "today") this.startDate = null;
            this.updateHeight();
        },
        promotionType() {
            this.updateHeight();
        },
        itemId() {
            this.fetchResourceItem();
        },
        resourceType() {
            this.fetchResourceItem();
        },
        resource(newVal) {
            this.default_image = this.resource?.image_link;
        },
        addSubtitle() {
            this.updateHeight();
        },
        addBtnText() {
            this.updateHeight();
        },
        addImage() {
            this.updateHeight();
        },
        addSecondaryBtn() {
            this.updateHeight();
        }
    },
    mounted() {
        const query = this.$route.query;

        // Assign route query params
        if (query.type) this.resourceType = query.type;
        if (query.id) this.itemId = query.id;

        // If editing an existing promotion (uuid present in route params)
        if (this.$route.params.uuid) {
            this.promotionUuid = this.$route.params.uuid;
            this.fetchItem(); // fetch live data for this promotion
        } else {
            // If creating new
            if (query.name) {
                this.name = this.$t('Promotion for') + ' ' + query.name;
                if (!this.customTitle) this.customTitle = this.name;
            }
        }
    }
}
</script>

<style scoped>
.promo-line .box {
    will-change: transform, background-color;
    transition: transform 0.3s ease;
    position: relative;
}

.promo-line.animate-line .special {
    animation: promoMoveSpecial 6s ease-in-out 0.5s infinite;
    /* start earlier */
    z-index: 10;
}

.promo-line.animate-line .box:nth-child(1),
.promo-line.animate-line .box:nth-child(2),
.promo-line.animate-line .box:nth-child(3) {
    animation: promoMoveOthers 6s ease-in-out 0.5s infinite;
    /* start earlier */
}

/* 🔸 Special orange box: moves left quickly, then holds, pause at end */
@keyframes promoMoveSpecial {

    0%,
    5% {
        transform: translateX(0);
        background-color: #e5e7eb;
        /* gray */
    }

    10% {
        background-color: var(--main_color);
        /* orange */
    }

    20% {
        transform: translateX(calc(-300% - 1.5rem));
        /* move fast */
        background-color: var(--main_color);
    }

    70% {
        transform: translateX(calc(-300% - 1.5rem));
        /* hold */
        background-color: var(--main_color);
    }

    80% {
        transform: translateX(0);
        /* move back fast */
        background-color: #e5e7eb;
    }

    90%,
    100% {
        transform: translateX(0);
        /* pause at end */
        background-color: #e5e7eb;
    }
}

/* 🔹 The first 3 boxes move right quickly, then hold, pause at end */
@keyframes promoMoveOthers {

    0%,
    5% {
        transform: translateX(0);
    }

    20% {
        transform: translateX(calc(100% + 0.5rem));
        /* move fast */
    }

    70% {
        transform: translateX(calc(100% + 0.5rem));
        /* hold */
    }

    80%,
    100% {
        transform: translateX(0);
        /* move back fast & pause */
    }
}</style>
